import { Fragment, type ReactNode } from "react"
import moment from "moment"
import PropTypes from "prop-types"
import { Box, Chip, Typography, type SxProps, type Theme } from "@mui/material"
import { InfoOutlined } from "@mui/icons-material"
import OPTooltip from "components/material/tooltip"
import { getPositionsBubblePart, getPositionsChipParts, getPricesChipParts, type ChipPart, type IntradayToolbarTimes } from "../helpers/intraday-toolbar-times"
import { getReportedPnlLine } from "../helpers/reported-pnl"

type Accent = "positions" | "performance"

const ACCENT_COLORS: Record<Accent, string> = {
  positions: "chart.factor",
  performance: "chart.trading",
}

const ROOT_SX = { display: "flex", alignItems: "center", gap: 1.5 } satisfies SxProps<Theme>
const TIME_SX = { display: "flex", alignItems: "center", gap: 0.5 } satisfies SxProps<Theme>
const CHIP_SX = { borderRadius: 1 } satisfies SxProps<Theme>
const CHIP_LABEL_SX: Record<Accent, SxProps<Theme>> = {
  positions: { color: ACCENT_COLORS.positions, fontWeight: "fontWeightBold", textTransform: "uppercase", letterSpacing: "0.04em" },
  performance: { color: ACCENT_COLORS.performance, fontWeight: "fontWeightBold", textTransform: "uppercase", letterSpacing: "0.04em" },
}
const CHIP_VALUE_SX = { fontFamily: "monospace", color: "text.primary" } satisfies SxProps<Theme>
const INFO_ICON_SX = { fontSize: 16, cursor: "pointer", color: "text.secondary" } satisfies SxProps<Theme>
const BUBBLE_SX = { display: "flex", flexDirection: "column", gap: 1.25, py: 0.5, maxWidth: 320 } satisfies SxProps<Theme>
const SECTION_SX = { display: "flex", alignItems: "flex-start", gap: 1 } satisfies SxProps<Theme>
const SECTION_BODY_SX = { display: "flex", flexDirection: "column", gap: 0.25 } satisfies SxProps<Theme>
const HEADING_SX = { fontWeight: "fontWeightBold" } satisfies SxProps<Theme>
const PNL_HEADING_SX = { fontWeight: "fontWeightBold", color: ACCENT_COLORS.positions } satisfies SxProps<Theme>
const DESCRIPTION_SX = { opacity: 0.75, lineHeight: 1.4 } satisfies SxProps<Theme>
const DOT_BASE_SX = { flexShrink: 0, width: 8, height: 8, mt: "6px", borderRadius: "50%", boxSizing: "border-box" } as const
const DOT_SX = {
  positions: { ...DOT_BASE_SX, bgcolor: ACCENT_COLORS.positions },
  performance: { ...DOT_BASE_SX, bgcolor: ACCENT_COLORS.performance },
  pnlPending: { ...DOT_BASE_SX, border: "1.5px solid", borderColor: ACCENT_COLORS.positions },
} satisfies Record<string, SxProps<Theme>>

type Dot = keyof typeof DOT_SX

const ACCENT_PROP = PropTypes.oneOf<Accent>(["positions", "performance"])

const CHIP_PARTS_SHAPE = PropTypes.arrayOf(
  PropTypes.shape({ label: PropTypes.string.isRequired, value: PropTypes.string.isRequired }).isRequired,
)

const REPORTED_PNL_SHAPE = PropTypes.shape({
  totalReturn: PropTypes.number,
  amount: PropTypes.number,
  asOf: PropTypes.string,
  isApplied: PropTypes.bool,
  basis: PropTypes.string,
})

const TIMES_SHAPE = PropTypes.shape({
  positionsAsOf: PropTypes.string,
  pricesAsOf: PropTypes.string,
  currency: PropTypes.string,
  reportedPnl: REPORTED_PNL_SHAPE,
})

export interface IntradayTimesInfoProps {
  data: IntradayToolbarTimes | null
  qualifiers: string[]
}

interface ChipPartsProps {
  parts: ChipPart[]
  accent: Accent
}

function ChipParts({ parts, accent }: ChipPartsProps) {
  return (
    <>
      {parts.map(({ label, value }, index) => (
        <Fragment key={label}>
          {index > 0 && " · "}
          <Box component="span" sx={CHIP_LABEL_SX[accent]}>{label}</Box>
          {" "}
          <Box component="span" sx={CHIP_VALUE_SX}>{value}</Box>
        </Fragment>
      ))}
    </>
  )
}

ChipParts.propTypes = {
  parts: CHIP_PARTS_SHAPE.isRequired,
  accent: ACCENT_PROP.isRequired,
}

interface BubbleSectionProps {
  dot: Dot
  heading: ReactNode
  descriptions: string[]
}

function BubbleSection({ dot, heading, descriptions }: BubbleSectionProps) {
  return (
    <Box sx={SECTION_SX}>
      <Box aria-hidden data-dot={dot} sx={DOT_SX[dot]} />
      <Box sx={SECTION_BODY_SX}>
        <Typography variant="body2" component="div">{heading}</Typography>
        {descriptions.map((description) => (
          <Typography key={description} variant="caption" component="div" sx={DESCRIPTION_SX}>{description}</Typography>
        ))}
      </Box>
    </Box>
  )
}

BubbleSection.propTypes = {
  dot: PropTypes.oneOf<Dot>(["positions", "performance", "pnlPending"]).isRequired,
  heading: PropTypes.node.isRequired,
  descriptions: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
}

interface BubbleHeadingProps {
  parts: ChipPart[]
}

function BubbleHeading({ parts }: BubbleHeadingProps) {
  return <Box component="span" sx={HEADING_SX}>{parts.map(({ label, value }) => `${label} · ${value}`).join(" · ")}</Box>
}

BubbleHeading.propTypes = {
  parts: CHIP_PARTS_SHAPE.isRequired,
}

interface PnlSectionProps {
  data: IntradayToolbarTimes
  qualifiers: string[]
}

function PnlSection({ data, qualifiers }: PnlSectionProps) {
  // Mounted on hover, so a stalled price feed still ages a pending P&L.
  const pnlLine = getReportedPnlLine(data.reportedPnl, data.currency, moment())
  if (!pnlLine) return null

  return (
    <BubbleSection
      dot={pnlLine.symbol === "●" ? "positions" : "pnlPending"}
      heading={<Box component="span" sx={PNL_HEADING_SX}>{pnlLine.text}</Box>}
      descriptions={[pnlLine.description, ...qualifiers]}
    />
  )
}

PnlSection.propTypes = {
  data: TIMES_SHAPE.isRequired,
  qualifiers: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
}

interface TimeInfoProps {
  label: string
  bubble: ReactNode
}

function TimeInfo({ label, bubble }: TimeInfoProps) {
  return (
    <OPTooltip title={<Box sx={BUBBLE_SX}>{bubble}</Box>}>
      <InfoOutlined titleAccess={label} sx={INFO_ICON_SX} />
    </OPTooltip>
  )
}

TimeInfo.propTypes = {
  label: PropTypes.string.isRequired,
  bubble: PropTypes.node.isRequired,
}

export default function IntradayTimesInfo({ data, qualifiers }: IntradayTimesInfoProps) {
  if (!data) return null
  const now = moment()
  const pricesChipParts = getPricesChipParts(data.pricesAsOf, now)
  const pnlSection = <PnlSection data={data} qualifiers={qualifiers} />

  return (
    <Box sx={ROOT_SX}>
      <Box sx={TIME_SX}>
        <Chip size="small" variant="outlined" sx={CHIP_SX} label={<ChipParts parts={getPositionsChipParts(data, now)} accent="positions" />} />
        <TimeInfo
          label="About the positions time"
          bubble={(
            <>
              <BubbleSection
                dot="positions"
                heading={<BubbleHeading parts={[getPositionsBubblePart(data, now)]} />}
                descriptions={["Your latest batch. Exposures, risk and composition."]}
              />
              {/* Without a prices time there is no performance tooltip to carry the P&L. */}
              {!pricesChipParts && pnlSection}
            </>
          )}
        />
      </Box>
      {pricesChipParts && (
        <Box sx={TIME_SX}>
          <Chip size="small" variant="outlined" sx={CHIP_SX} label={<ChipParts parts={pricesChipParts} accent="performance" />} />
          <TimeInfo
            label="About the performance time"
            bubble={(
              <>
                <BubbleSection
                  dot="performance"
                  heading={<BubbleHeading parts={pricesChipParts} />}
                  descriptions={["Returns and attribution, prices about 15 minutes delayed."]}
                />
                {pnlSection}
              </>
            )}
          />
        </Box>
      )}
    </Box>
  )
}

IntradayTimesInfo.propTypes = {
  data: TIMES_SHAPE,
  qualifiers: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
}
